/**
 * Casbin Context and Hook
 * Provides authorization state throughout the React component tree
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Authorizer } from 'casbin.js';
import { apiCall } from '@/util/api';
import { useUserContext } from '../modules/user/useUserContext';
import { CLAIMS_LEVEL, type ClaimsLevel } from '@/modules/idv';
import { deletePhoneAndEmailVerifications } from '@/services/iam';

// Casbin constants matching backend
export const RESOURCES = {
  TOPICS: 'topics',
  TOPIC_REGISTRATIONS: 'topic_registrations',
  MESSAGES: 'messages',
  MESSAGE_RECIPIENTS: 'message_recipients',
  RAW_TEMPLATES: 'raw_templates',
} as const;

export const ACTIONS = {
  LIST: 'list',
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  SHARE: 'share',
} as const;

interface CasbinContextType {
  authorizer: Authorizer | null;
  isInitialized: boolean;
  initialize: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  can: (
    action: string,
    resource: string,
    resourceId?: string
  ) => Promise<boolean>;
  // could answers the hypothetical "what verification level (if any) would let
  // the principal do this?" — the scoped analogue of the unlockable permission
  // maps. Resolves to the minimal ClaimsLevel that would grant the operation
  // (UNVERIFIED = already allowed) or null when no upgrade would ever grant it.
  // Unlike `can`'s scoped path it also runs for guests (a guest holding a
  // PendingInvite claim can learn that linking a phone unlocks a private invite).
  could: (
    action: string,
    resource: string,
    resourceId?: string
  ) => Promise<ClaimsLevel | null>;
  permissionsMap: Record<string, string[]>;
  claimUnlockingMap: Record<string, ClaimsLevel | null>;
}

const CasbinContext = createContext<CasbinContextType | undefined>(undefined);

interface CasbinProviderProps {
  children: React.ReactNode;
}

export const CasbinProvider: React.FC<CasbinProviderProps> = ({ children }) => {
  // Get UserContext - permissions will refresh whenever any UserContext property changes
  const userContext = useUserContext();
  // Extract specific values to avoid unnecessary re-renders when object reference changes
  const { userId, claims, loading } = userContext;
  const hasUserId = userContext.hasUserId;
  // Create stable string representation of claims for dependency comparison
  // Serialize expiration dates to avoid re-renders when object reference changes
  const claimsKey = useMemo(
    () =>
      JSON.stringify({
        phone: claims.phoneVerifExp,
        email: claims.emailVerifExp,
      }),
    [claims.phoneVerifExp, claims.emailVerifExp]
  );

  const [authorizer, setAuthorizer] = useState<Authorizer | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Cache for scoped permission checks (action:resource:resourceId -> Promise<boolean>).
  // Stores promises so concurrent calls for the same key share one in-flight request.
  // Cleared on every permission refresh to stay consistent with updated server state.
  const scopedPermissionCache = useRef(new Map<string, Promise<boolean>>());

  // Same-shaped cache for scoped could() probes (the minimal ClaimsLevel — or
  // null — that would grant the operation). Uncached, could() round-trips per
  // call, and on-demand guards probe it for every button; caching dedupes.
  const scopedCouldCache = useRef(
    new Map<string, Promise<ClaimsLevel | null>>()
  );

  // Store permissions in state so consumers re-render when they change.
  // Exposed as `permissionsMap` on the context — consumers read it directly
  // (e.g. permissionsMap[action]?.includes(resource)) and use the object
  // identity as a useEffect dependency for scoped checks.
  const [permissionsMap, setPermissionsMap] = useState<
    Record<string, string[]>
  >({});

  // Store unlockable permissions grouped by trust level
  const [unlockablePermissions, setUnlockablePermissions] = useState<{
    current: Record<string, string[]>;
    phone: Record<string, string[]>;
    email: Record<string, string[]>;
    phoneOrEmail: Record<string, string[]>;
  }>({
    current: {},
    phone: {},
    email: {},
    phoneOrEmail: {},
  });

  // Create authorizer on mount
  useEffect(() => {
    // Use 'manual' mode instead of 'auto' to have more control
    const newAuthorizer = new Authorizer('manual');
    setAuthorizer(newAuthorizer);
  }, []);

  // Helper function to process and store permissions from API response
  const processPermissionsResponse = useCallback(
    (responseData: {
      actions_for_current_trust?: Record<string, string[]>;
      actions_for_phone_trust?: Record<string, string[]>;
      actions_for_email_trust?: Record<string, string[]>;
      actions_for_phone_or_email_trust?: Record<string, string[]>;
    }) => {
      // Response format: { actions_for_current_trust: {...}, actions_for_phone_trust: {...}, actions_for_email_trust: {...}, actions_for_phone_or_email_trust: {...} }
      const currentPermissions = responseData.actions_for_current_trust || {};
      const phoneUnlockable = responseData.actions_for_phone_trust || {};
      const emailUnlockable = responseData.actions_for_email_trust || {};
      const phoneOrEmailUnlockable =
        responseData.actions_for_phone_or_email_trust || {};

      // Invalidate cached scoped checks — server-side permissions may have changed.
      scopedPermissionCache.current.clear();
      scopedCouldCache.current.clear();

      // Store current permissions locally (don't trust authorizer.can())
      if (authorizer) {
        authorizer.setPermission(currentPermissions);
      }
      setPermissionsMap(currentPermissions);

      // Store unlockable permissions
      setUnlockablePermissions({
        current: currentPermissions,
        phone: phoneUnlockable,
        email: emailUnlockable,
        phoneOrEmail: phoneOrEmailUnlockable,
      });

      return currentPermissions;
    },
    [authorizer]
  );

  const initialize = useCallback(async () => {
    if (!authorizer) {
      // eslint-disable-next-line no-console
      console.warn('Casbin authorizer not ready yet');
      return;
    }

    try {
      // Backend extracts user ID and trust level from JWT token, no query params needed
      const endpoint = `/api/casbin-permission`;
      const response = await apiCall(endpoint);

      // Only treat as invalid token if we get an authentication error (401/403)
      // Empty permissions might be legitimate if the trust level has no permissions
      // or if policies haven't been initialized yet
      if (response.status === 401 || response.status === 403) {
        // Authentication/authorization error - token might be invalid
        // eslint-disable-next-line no-console
        console.warn(
          'Authentication error detected - resetting to guest claims'
        );

        // Assume guest claims to get a fresh valid token
        await deletePhoneAndEmailVerifications();
        return;
      }

      // Process the permissions response if successful
      if (response.ok) {
        const responseData = await response.json();
        processPermissionsResponse(responseData);
        setIsInitialized(true); // Only set to true on success
      }
      // If response.ok is false, don't set isInitialized - permissions weren't loaded
    } catch (error) {
      // Don't set isInitialized here - we failed to load permissions
    }
  }, [authorizer, processPermissionsResponse]);

  // Refresh permissions when UserContext changes
  const refreshPermissions = useCallback(async () => {
    if (!authorizer) {
      return;
    }

    try {
      // Backend extracts user ID and trust level from JWT token, no query params needed
      const endpoint = `/api/casbin-permission`;
      const response = await apiCall(endpoint);

      if (response.ok) {
        const responseData = await response.json();
        processPermissionsResponse(responseData);
        setIsInitialized(true); // Only set to true on success
      }
      // If response.ok is false, don't set isInitialized - permissions weren't loaded
    } catch (error) {
      // Silently handle errors - don't set isInitialized since we failed to load permissions
      // Don't set isInitialized here - we failed to load permissions
    }
  }, [authorizer, processPermissionsResponse]);

  // Refresh permissions whenever UserContext changes
  useEffect(() => {
    if (!authorizer || loading) {
      return;
    }

    refreshPermissions();
  }, [authorizer, userId, claimsKey, loading, refreshPermissions]);

  const can = useCallback(
    async (
      action: string,
      resource: string,
      resourceId?: string
    ): Promise<boolean> => {
      if (!authorizer) {
        return false;
      }

      // If resourceId is provided and user has a profile, check scoped permissions via API.
      // Guests can't hold scoped role assignments, so skip the API call and fall through
      // to the general permissionsMap check below.
      if (resourceId !== undefined && hasUserId) {
        const cacheKey = `${action}:${resource}:${resourceId}`;
        const cached = scopedPermissionCache.current.get(cacheKey);
        if (cached !== undefined) {
          return cached;
        }
        const promise = (async () => {
          try {
            const encodedResourceId = encodeURIComponent(resourceId);
            const response = await apiCall(
              `/api/casbin-can?action=${encodeURIComponent(action)}&resource=${encodeURIComponent(resource)}&resource_id=${encodedResourceId}`
            );
            if (response.ok) {
              const data = await response.json();
              return data.allowed === true;
            }
            return false;
          } catch (error) {
            return false;
          }
        })();
        scopedPermissionCache.current.set(cacheKey, promise);
        return promise;
      }

      // Check locally stored permissions (don't trust authorizer.can())
      const resourceList = permissionsMap[action];
      return Array.isArray(resourceList) && resourceList.includes(resource);
    },
    [authorizer, hasUserId, permissionsMap]
  );

  const could = useCallback(
    async (
      action: string,
      resource: string,
      resourceId?: string
    ): Promise<ClaimsLevel | null> => {
      const fetchLevel = async (): Promise<ClaimsLevel | null> => {
        try {
          const params = new URLSearchParams({ action, resource });
          if (resourceId !== undefined) {
            params.set('resource_id', resourceId);
          }
          const response = await apiCall(
            `/api/casbin-could?${params.toString()}`
          );
          if (!response.ok) {
            return null;
          }
          const data = await response.json();
          // "" means no upgrade would ever grant the operation.
          return data.level ? (data.level as ClaimsLevel) : null;
        } catch {
          return null;
        }
      };

      // Cache scoped probes (deduped per action:resource:resourceId, cleared on
      // every permission refresh). Unscoped probes are rare — skip the cache.
      if (resourceId === undefined) {
        return fetchLevel();
      }
      const cacheKey = `${action}:${resource}:${resourceId}`;
      const cached = scopedCouldCache.current.get(cacheKey);
      if (cached !== undefined) {
        return cached;
      }
      const promise = fetchLevel();
      scopedCouldCache.current.set(cacheKey, promise);
      return promise;
    },
    []
  );

  // Precomputed lookup for the verification level that would unlock each
  // (action, resource) pair. Key format: `${action}:${resource}`.
  // Priority mirrors the previous getClaimsThatWouldUnlock:
  // PHONE_AND_EMAIL > PHONE > EMAIL > PHONE_OR_EMAIL > null.
  const claimUnlockingMap = useMemo<Record<string, ClaimsLevel | null>>(() => {
    const map: Record<string, ClaimsLevel | null> = {};
    const collectKeys = (dict: Record<string, string[]>) => {
      for (const [action, resources] of Object.entries(dict)) {
        for (const resource of resources) {
          map[`${action}:${resource}`] = null;
        }
      }
    };
    collectKeys(unlockablePermissions.phone);
    collectKeys(unlockablePermissions.email);
    collectKeys(unlockablePermissions.phoneOrEmail);

    for (const key of Object.keys(map)) {
      const [action, resource] = key.split(':');
      const phone =
        unlockablePermissions.phone[action]?.includes(resource) || false;
      const email =
        unlockablePermissions.email[action]?.includes(resource) || false;
      const phoneOrEmail =
        unlockablePermissions.phoneOrEmail[action]?.includes(resource) || false;

      if (phone && email) {
        map[key] = CLAIMS_LEVEL.PHONE_AND_EMAIL;
      } else if (phone) {
        map[key] = CLAIMS_LEVEL.PHONE;
      } else if (email) {
        map[key] = CLAIMS_LEVEL.EMAIL;
      } else if (phoneOrEmail) {
        map[key] = CLAIMS_LEVEL.PHONE_OR_EMAIL;
      }
    }

    return map;
  }, [unlockablePermissions]);

  const value: CasbinContextType = useMemo(
    () => ({
      authorizer,
      isInitialized,
      initialize,
      refreshPermissions,
      can,
      could,
      permissionsMap,
      claimUnlockingMap,
    }),
    [
      authorizer,
      isInitialized,
      initialize,
      refreshPermissions,
      can,
      could,
      permissionsMap,
      claimUnlockingMap,
    ]
  );

  // Surface initialization state to e2e tests as a DOM attribute on ion-app.
  // ActionGuard renders on-demand buttons disabled until permissions load, but
  // Cypress can't directly observe React state — this lets `cy.waitForCasbinReady`
  // gate clicks on the same condition the production component is gating on.
  useEffect(() => {
    const root = document.querySelector('ion-app');
    if (!root) return;
    if (isInitialized) {
      root.setAttribute('data-casbin-initialized', 'true');
    } else {
      root.removeAttribute('data-casbin-initialized');
    }
  }, [isInitialized]);

  return (
    <CasbinContext.Provider value={value}>{children}</CasbinContext.Provider>
  );
};

/**
 * Hook to access Casbin authorization
 * @throws Error if used outside of CasbinProvider
 */
export const useCasbin = (): CasbinContextType => {
  const context = useContext(CasbinContext);
  if (context === undefined) {
    throw new Error('useCasbin must be used within a CasbinProvider');
  }
  return context;
};
